import React, { useMemo } from 'react';
import { ColumnsType } from 'antd/es/table';
import { Space } from 'antd';
import {
	DATE_AND_TIME,
	PortActionTypeLabels,
	PortActionTypes,
	TimeZoneFormat,
} from '@shared/utils/constants';
import { toMoment } from '@shared/utils/date';
import { Values } from '@shared/utils/objectEnums';
import { ItineraryPortCallDto } from '@client/screens/estimates/details/helpers/types';
import Table from '@client/components/Table/Table';
import CountryFlag from '@client/components/CountryFlag';
import DateBadge from '@client/screens/fleet/VoyageDetailsScreen/components/ItineraryTab/components/DateBadge';
import { formatDate } from '@client/utils/formatDate';
import styles from './styles/PositionListItineraryTable.module.css';

const actionsToShow = [
	PortActionTypes.COMMENCEMENT,
	PortActionTypes.COMPLETION,
	PortActionTypes.BUNKER_STEM,
	PortActionTypes.DELIVERY,
	PortActionTypes.REDELIVERY,
	PortActionTypes.REDELIVERING_TC_IN,
	PortActionTypes.ARRIVAL,
	PortActionTypes.DEPARTURE,
	PortActionTypes.LOADING,
	PortActionTypes.DISCHARGING,
	PortActionTypes.CANAL_TRANSIT,
] as Values<typeof PortActionTypes>[];

const formatArrival = (row: ItineraryPortCallDto) => {
	const dateToUse = row.arrivalDate ?? row.mastersEta ?? row.estimatedArrivalDate;

	if (dateToUse == null) {
		return '—';
	}

	return (
		<Space size="small">
			<DateBadge entry={row} type="arrival" />
			{formatDate(
				toMoment(dateToUse).tz(row.port?.timezone),
				DATE_AND_TIME,
				true,
				TimeZoneFormat.LocalTime,
			)}
		</Space>
	);
};

const formatDeparture = (row: ItineraryPortCallDto) => {
	const dateToUse = row.departureDate ?? row.estimatedDepartureDate;

	if (dateToUse == null) {
		return '—';
	}

	return (
		<Space size="small">
			<DateBadge entry={row} type="departure" />
			{formatDate(
				toMoment(dateToUse).tz(row.port?.timezone),
				DATE_AND_TIME,
				true,
				TimeZoneFormat.LocalTime,
			)}
		</Space>
	);
};

const getActivitiesLabel = (row: ItineraryPortCallDto) => {
	if (row?.actions == null) {
		return row.cargoActivity ?? '—';
	}

	const filtered = row.actions.filter((a) => {
		if (a.event === PortActionTypes.ARRIVAL || a.event === PortActionTypes.DEPARTURE) {
			return false;
		}

		if (a.event === PortActionTypes.LOADING || a.event === PortActionTypes.DISCHARGING) {
			return true;
		}

		return actionsToShow.includes(a.event as Values<typeof PortActionTypes>);
	});

	const labels = filtered
		.map((a) => PortActionTypeLabels[a.event as keyof typeof PortActionTypeLabels] || a.event);

	if (row.cargoActivity) {
		labels.push(row.cargoActivity);
	}

	return labels.length > 0 ? labels.join(', ') : '—';
};

const getCargoLabel = (row: ItineraryPortCallDto) => {
	if (row.cargoCommodity != null) {
		return row.cargoCommodity;
	}

	if (row.actions == null) {
		return '—';
	}

	const cargoTypes = row.actions
		.filter((a) => a.event === PortActionTypes.LOADING || a.event === PortActionTypes.DISCHARGING)
		.map((a) => a?.Cargo?.type)
		.filter(Boolean);

	return cargoTypes.length > 0 ? [...new Set(cargoTypes)].join(', ') : '—';
};

type PositionListItineraryTableProps = {
	portCalls: ItineraryPortCallDto[];
	loading?: boolean;
};

const PositionListItineraryTable = ({
	portCalls,
	loading = false,
}: PositionListItineraryTableProps) => {
	const columns = useMemo<ColumnsType<ItineraryPortCallDto>>(() => [
		{
			title: 'Port',
			key: 'port',
			width: 160,
			ellipsis: true,
			render: (_, row) => (
				row.port == null ? '—' : (
					<Space size="small">
						<CountryFlag countryCode={row.port.countryCode} />
						<span title={row.port.name}>{row.port.name}</span>
					</Space>
				)
			),
		},
		{
			title: 'ATA / Master\'s ETA / ETA',
			key: 'arrival',
			width: 200,
			render: (_, row) => formatArrival(row),
		},
		{
			title: 'ATD / ETD',
			key: 'departure',
			width: 200,
			render: (_, row) => formatDeparture(row),
		},
		{
			title: 'Activities',
			key: 'activities',
			width: 180,
			ellipsis: true,
			render: (_, row) => getActivitiesLabel(row),
		},
		{
			title: 'Cargo',
			key: 'cargo',
			width: 120,
			ellipsis: true,
			render: (_, row) => getCargoLabel(row),
		},
	], []);

	return (
		<Table<ItineraryPortCallDto>
			className={styles.table}
			size="small"
			pagination={false}
			loading={loading}
			rowKey="id"
			dataSource={portCalls}
			columns={columns}
			tableLayout="fixed"
		/>
	);
};

export {
	formatArrival,
	formatDeparture,
	getActivitiesLabel,
	getCargoLabel,
};

export default PositionListItineraryTable;
